import { SuperAdminSettings } from "@/types/dataManagement.types";
import strings from "../tenantSettings.translations";
import { SettingsGridRow } from "./GenericSettingsGrid";

export const TENANT_FEATURE_KEYS = {
  clockInOut: "features.clockInOut",
  pay: "features.pay",
} as const;

export type FeatureOverrideValue = "default" | "enabled" | "disabled";

export type SuperAdminSettingsProperties = {
  [TENANT_FEATURE_KEYS.clockInOut]?: boolean;
  [TENANT_FEATURE_KEYS.pay]?: boolean;
  [key: string]: any;
};

export interface BackstageFeatures {
  clockIn: boolean;
  pay: boolean;
}

export const parseSuperAdminSettingsProperties = (properties?: string | null): SuperAdminSettingsProperties => {
  if (!properties) {
    return {};
  }

  try {
    const parsed = JSON.parse(properties);
    return parsed && typeof parsed === "object" ? (parsed as SuperAdminSettingsProperties) : {};
  } catch {
    return {};
  }
};

const getFeatureOverrideValue = (value: unknown): FeatureOverrideValue => {
  if (value === true) {
    return "enabled";
  }

  if (value === false) {
    return "disabled";
  }

  return "default";
};

const secondsToHHMMSS = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(v => String(v).padStart(2, "0")).join(":");
};

export const mapSuperAdminSettingsToRowData = (
  data: SuperAdminSettings,
  backstageFeatures: BackstageFeatures
): SettingsGridRow[] => {
  const rows: SettingsGridRow[] = [];
  const properties = parseSuperAdminSettingsProperties(data.properties);

  rows.push({
    id: "super-admin-settings",
    label: strings.superAdmin.sectionTitle,
    description: "",
    isSection: true,
  });

  if (properties.canDeleteRosters != null) {
    rows.push({
      id: "canDeleteRosters",
      label: strings.superAdmin.canDeleteRostersLabel,
      description: strings.superAdmin.canDeleteRostersDescription,
      value: properties.canDeleteRosters,
      type: "boolean",
      isSection: false,
    });
  }

  if (properties.displayEmployeeIds != null) {
    rows.push({
      id: "displayEmployeeIds",
      label: strings.superAdmin.displayEmployeeIdsLabel,
      description: strings.superAdmin.displayEmployeeIdsDescription,
      value: properties.displayEmployeeIds,
      type: "boolean",
      isSection: false,
    });
  }

  if (properties.maxCustomTemplates != null) {
    rows.push({
      id: "maxCustomTemplates",
      label: strings.superAdmin.maxCustomTemplatesLabel,
      description: strings.superAdmin.maxCustomTemplatesDescription,
      value: properties.maxCustomTemplates,
      type: "select",
      options: [
        { label: "5", value: 5 },
        { label: "10", value: 10 },
        { label: "15", value: 15 },
        { label: "20", value: 20 },
      ],
      isSection: false,
    });
  }

  if (properties.historicalData != null) {
    rows.push({
      id: "historicalData",
      label: strings.superAdmin.historicalDataLabel,
      description: strings.superAdmin.historicalDataDescription,
      value: properties.historicalData,
      type: "select",
      options: [
        { label: "1", value: 1 },
        { label: "2", value: 2 },
        { label: "3", value: 3 },
        { label: "4", value: 4 },
        { label: "5", value: 5 },
        { label: "6", value: 6 },
        { label: "7", value: 7 },
        { label: "8", value: 8 },
        { label: "9", value: 9 },
        { label: "10", value: 10 },
        { label: "11", value: 11 },
        { label: "12", value: 12 },
        { label: "13", value: 13 },
        { label: "14", value: 14 },
        { label: "15", value: 15 },
      ],
      isSection: false,
    });
  }

  rows.push(
    {
      id: "section-leave-settings",
      label: strings.superAdmin.sectionLeaveSettings,
      description: strings.superAdmin.sectionLeaveSettingsDescription,
      isSection: true,
    },
    {
      id: "leaveTypeTier",
      label: strings.superAdmin.leaveTypeTierLabel,
      description: strings.superAdmin.leaveTypeTierDescription,
      value: data.leaveTypeTier,
      type: "select",
      options: [
        { value: 0, label: strings.superAdmin.leaveTypeTierStandard },
        { value: 1, label: strings.superAdmin.leaveTypeTierAdvanced },
      ],
      isSection: false,
    }
  );

  if (properties.advancedContractsAndRoleManagement != null) {
    rows.push(
      {
        id: "contacts-and-roles",
        label: strings.superAdmin.sectionContactsRoles,
        description: "",
        isSection: true,
      },
      {
        id: "advancedContractsAndRoleManagement",
        label: strings.superAdmin.advancedContractsLabel,
        description: strings.superAdmin.advancedContractsDescription,
        value: properties.advancedContractsAndRoleManagement,
        type: "boolean",
        isSection: false,
      }
    );
  }

  rows.push({
    id: "section-advanced-properties",
    label: strings.superAdmin.sectionAdvancedProperties,
    description: strings.superAdmin.sectionAdvancedPropertiesDescription,
    isSection: true,
  });

  if (properties.optimiserDuration != null) {
    rows.push({
      id: "optimiserDuration",
      label: strings.superAdmin.optimiserDurationLabel,
      description: strings.superAdmin.optimiserDurationDescription,
      value: secondsToHHMMSS(properties.optimiserDuration),
      type: "timespan",
      range: {
        start: "00:00:30",
        end: "00:03:00",
        step: 10,
        unit: "seconds",
        displayHHmm: false,
      },
      isSection: false,
    });
  }

  if (properties.optimiserManagementAttendance != null) {
    rows.push({
      id: "optimiserManagementAttendance",
      label: strings.superAdmin.optimiserManagementAttendanceLabel,
      description: strings.superAdmin.optimiserManagementAttendanceDescription,
      value: properties.optimiserManagementAttendance,
      type: "boolean",
      isSection: false,
    });
  }

  rows.push(
    {
      id: "section-feature-availability",
      label: strings.superAdmin.sectionFeatureAvailability,
      description: strings.superAdmin.sectionFeatureAvailabilityDescription,
      isSection: true,
    },
    backstageFeatures.clockIn
      ? {
          id: TENANT_FEATURE_KEYS.clockInOut,
          label: strings.superAdmin.clockInOutLabel,
          description: strings.superAdmin.clockInOutDescription,
          value: getFeatureOverrideValue(properties[TENANT_FEATURE_KEYS.clockInOut]),
          type: "select",
          options: [
            { label: strings.superAdmin.featureDefault, value: "default" },
            { label: strings.superAdmin.featureEnabled, value: "enabled" },
            { label: strings.superAdmin.featureDisabled, value: "disabled" },
          ],
          isSection: false,
        }
      : {
          id: TENANT_FEATURE_KEYS.clockInOut,
          label: strings.superAdmin.clockInOutLabel,
          description: strings.superAdmin.clockInOutDescription,
          value: strings.superAdmin.featureDisabledByBackstage,
          type: "text",
          isSection: false,
          disabled: true,
        },
    backstageFeatures.pay
      ? {
          id: TENANT_FEATURE_KEYS.pay,
          label: strings.superAdmin.payLabel,
          description: strings.superAdmin.payDescription,
          value: getFeatureOverrideValue(properties[TENANT_FEATURE_KEYS.pay]),
          type: "select",
          options: [
            { label: strings.superAdmin.featureDefault, value: "default" },
            { label: strings.superAdmin.featureEnabled, value: "enabled" },
            { label: strings.superAdmin.featureDisabled, value: "disabled" },
          ],
          isSection: false,
        }
      : {
          id: TENANT_FEATURE_KEYS.pay,
          label: strings.superAdmin.payLabel,
          description: strings.superAdmin.payDescription,
          value: strings.superAdmin.featureDisabledByBackstage,
          type: "text",
          isSection: false,
          disabled: true,
        }
  );

  return rows;
};

export const mapRowDataToSuperAdminSettings = (
  snapshot: SuperAdminSettings,
  rowData: SettingsGridRow[]
): SuperAdminSettings => {
  const properties = parseSuperAdminSettingsProperties(snapshot.properties);

  const optimiserDurationRow = rowData.find(r => r.id === "optimiserDuration");
  if (optimiserDurationRow?.value) {
    const [h, m, s] = (optimiserDurationRow.value as string).split(":").map(Number);
    properties.optimiserDuration = h * 3600 + m * 60 + s;
  }

  const optimiserManagementAttendanceRow = rowData.find(r => r.id === "optimiserManagementAttendance");
  if (optimiserManagementAttendanceRow) {
    properties.optimiserManagementAttendance = optimiserManagementAttendanceRow.value;
  }

  const canDeleteRostersRow = rowData.find(r => r.id === "canDeleteRosters");
  if (canDeleteRostersRow) {
    properties.canDeleteRosters = canDeleteRostersRow.value;
  }

  const displayEmployeeIdsRow = rowData.find(r => r.id === "displayEmployeeIds");
  if (displayEmployeeIdsRow) {
    properties.displayEmployeeIds = displayEmployeeIdsRow.value;
  }

  const maxCustomTemplatesRow = rowData.find(r => r.id === "maxCustomTemplates");
  if (maxCustomTemplatesRow?.value) {
    properties.maxCustomTemplates = maxCustomTemplatesRow.value as number;
  }

  const historicalDataRow = rowData.find(r => r.id === "historicalData");
  if (historicalDataRow?.value) {
    properties.historicalData = historicalDataRow.value as number;
  }

  const advancedContractsAndRoleManagementRow = rowData.find(r => r.id === "advancedContractsAndRoleManagement");
  if (advancedContractsAndRoleManagementRow) {
    properties.advancedContractsAndRoleManagement = advancedContractsAndRoleManagementRow.value;
  }

  const leaveTypeTier = rowData.find(r => r.id === "leaveTypeTier")?.value as number | undefined;

  const clockInOutRowValue = rowData.find(r => r.id === TENANT_FEATURE_KEYS.clockInOut)?.value;
  if (clockInOutRowValue === "enabled") {
    properties[TENANT_FEATURE_KEYS.clockInOut] = true;
  } else if (clockInOutRowValue === "disabled") {
    properties[TENANT_FEATURE_KEYS.clockInOut] = false;
  } else if (clockInOutRowValue === "default") {
    delete properties[TENANT_FEATURE_KEYS.clockInOut];
  }

  const payRowValue = rowData.find(r => r.id === TENANT_FEATURE_KEYS.pay)?.value;
  if (payRowValue === "enabled") {
    properties[TENANT_FEATURE_KEYS.pay] = true;
  } else if (payRowValue === "disabled") {
    properties[TENANT_FEATURE_KEYS.pay] = false;
  } else if (payRowValue === "default") {
    delete properties[TENANT_FEATURE_KEYS.pay];
  }

  return {
    ...snapshot,
    leaveTypeTier: leaveTypeTier ?? snapshot.leaveTypeTier,
    properties: JSON.stringify(properties),
  };
};
